/**
 * MjToolbar — Midjourney 工具条组件
 *
 * 两个阶段：
 *   Stage 1 (grid) — 红色工具条：放大 / 变体 / 重做
 *   Stage 2 (upscaled) — 深黄色工具条：细腻放大 / 创意放大 / 缩放 / 平移 / 强变体 / 弱变体 / 局部重绘(灰)
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  ZoomIn, Shuffle, RefreshCw, Sparkles, Palette,
  Maximize, Move, Wand2, Paintbrush, Sparkle
} from 'lucide-react';
import QuickTooltip from '../components/QuickTooltip';

// MJ logo (简化 diamond icon)
const MjLogo = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <path d="M12 2L2 12l10 10 10-10L12 2z" fill="currentColor" opacity="0.9" />
  </svg>
);

// ── 四宫格选择器弹窗 ──
function GridSelector({ onSelect, onClose, label }) {
  return (
    <div style={popupStyles.container}>
      <div style={popupStyles.title}>{label}：选择图片</div>
      <div style={popupStyles.grid}>
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            onClick={() => { onSelect(i); onClose(); }}
            style={popupStyles.cell}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          >
            <span style={{ fontSize: 16, fontWeight: 700 }}>{i}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 缩放选择器弹窗 ──
function ZoomSelector({ onSelect, onClose }) {
  return (
    <div style={{ ...popupStyles.container, width: 120 }}>
      <div style={popupStyles.title}>缩放倍率</div>
      {['2x', '1.5x'].map(level => (
        <div key={level} onClick={() => { onSelect(level); onClose(); }}
          style={popupStyles.option}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >{level}</div>
      ))}
    </div>
  );
}

// ── 平移方向选择器 ──
function PanSelector({ onSelect, onClose }) {
  const ArrowIcon = ({ direction }) => {
    const rotationMap = {
      up: 0,
      right: 90,
      down: 180,
      left: 270,
    };

    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        style={{ transform: `rotate(${rotationMap[direction]}deg)` }}
      >
        <path d="M8 13V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M4.5 6.5L8 3l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const dirs = [
    { key: 'up', hint: '向上延长' },
    { key: 'right', hint: '向右延长' },
    { key: 'left', hint: '向左延长' },
    { key: 'down', hint: '向下延长' },
  ];

  return (
    <div style={popupStyles.container}>
      <div style={popupStyles.title}>平移方向</div>
      <div style={popupStyles.grid}>
        {dirs.map(d => (
          <div
            key={d.key}
            onClick={() => { onSelect(d.key); onClose(); }}
            style={{ ...popupStyles.cell, flexDirection: 'column', gap: 2 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            title={d.hint}
          >
            <span style={{ lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowIcon direction={d.key} />
            </span>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>{d.hint}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ToolButton (工具条内的单个按钮) ──
function ToolButton({ icon: Icon, label, onClick, disabled, color }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="nodrag"
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={disabled ? '即将推出' : label}
      style={{
        display: 'flex', alignItems: 'center', gap: 3,
        padding: '2px 8px', borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
        background: hover && !disabled ? 'rgba(255,255,255,0.15)' : 'transparent',
        opacity: disabled ? 0.35 : 1,
        transition: 'background 0.15s',
        fontSize: 10, color: color || 'rgba(255,255,255,0.9)',
        whiteSpace: 'nowrap',
      }}
    >
      <span>{label}</span>
    </div>
  );
}

// ════════════════════════════════════════════════════
// MjToolbar 主组件
// ════════════════════════════════════════════════════
// 根据节点实际 mjButtons 判断某个动作是否可用
// MJ API 对不同链路（如 inpaint→upscale）返回不同的按钮集合
// 没有对应按钮时隐藏工具条按钮，避免用户点击后报错
function hasApiButton(actionType, mjButtons) {
  if (!mjButtons || mjButtons.length === 0) return true; // 无按钮数据时全部显示（兼容旧节点）
  return mjButtons.some(b => {
    const cid = (b.customId || '').toLowerCase();
    const lbl = (b.label || '').toLowerCase();
    switch (actionType) {
      case 'subtle_upscale':   return (cid.includes('subtle') && (cid.includes('upsample') || cid.includes('upscale'))) || lbl === 'upscale (subtle)';
      case 'creative_upscale': return (cid.includes('creative') && (cid.includes('upsample') || cid.includes('upscale'))) || lbl === 'upscale (creative)';
      case 'high_variation':   return cid.includes('high_variation') || lbl.includes('vary (strong)');
      case 'low_variation':    return cid.includes('low_variation') || lbl.includes('vary (subtle)');
      case 'zoom':             return cid.includes('outpaint') || cid.includes('zoom');
      case 'pan':              return cid.includes('pan_');
      case 'inpaint':          return cid.includes('inpaint') || lbl.includes('vary (region)');
      default:                 return true;
    }
  });
}

export default function MjToolbar({ phase, nodeId, onAction, nodeWidth, zoom = 1, toolboxName, toolboxLogo, actionLabels = {}, disabledActions = [], creditCost = 0, mjButtons, mjActionType, vendor }) {
  const [popup, setPopup] = useState(null); // 'upscale' | 'variation' | 'zoom' | 'pan' | null
  const toolbarRef = useRef(null);

  // 点击工具栏外部或画布拖动时关闭弹窗
  useEffect(() => {
    if (!popup) return;
    const handleClose = (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        setPopup(null);
      }
    };
    // mousedown 能捕获画布拖动的起始动作
    document.addEventListener('mousedown', handleClose, true);
    return () => document.removeEventListener('mousedown', handleClose, true);
  }, [popup]);

  const isGrid = phase === 'grid';
  const hidden = new Set(disabledActions);
  // APImart 渠道不支持细腻放大/创意放大（无对应端点），直接隐藏
  const isApimart = vendor === 'apimart-midjourney';
  if (isApimart) {
    hidden.add('subtle_upscale');
    hidden.add('creative_upscale');
  }
  // 第二次放大（subtle/creative upscale）产生的节点不支持 inpaint
  const isSecondaryUpscale = mjActionType === 'subtle_upscale' || mjActionType === 'creative_upscale';
  // 额外隐藏 MJ API 未返回对应按钮的动作（如 inpaint→upscale 链路无 subtle/creative/variation）
  const canShow = (action) => {
    if (hidden.has(action)) return false;
    if (action === 'inpaint' && isSecondaryUpscale) return false;
    return hasApiButton(action, mjButtons);
  };

  const handleAction = (actionType, params = {}) => {
    if (onAction) onAction(nodeId, actionType, params);
  };

  // 胶囊条样式（匹配节点底部蓝色工具栏的 capsuleStyle）
  const capsuleBar = {
    display: 'flex', alignItems: 'center', gap: 1,
    background: isGrid ? '#3d1a1e' : '#3d2e12',
    border: `1px solid ${isGrid ? 'rgba(200,70,70,0.3)' : 'rgba(200,160,50,0.3)'}`,
    borderRadius: '50px', padding: '4px 10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    pointerEvents: 'auto', width: 'max-content',
    userSelect: 'none',
    backdropFilter: 'blur(8px)',
  };

  const divider = { width: 1, height: 14, background: 'rgba(255,255,255,0.2)', margin: '0 3px' };

  // ── 动态定位：工具条严格保持在标题文字上方 5px ──
  // 标题栏 CSS: top=-20px, 元素高度≈22px, transformOrigin='bottom left' + scale(1/zoom)
  //   → 标题底边固定在: -20 + 22 = 2px
  //   → 标题视觉顶部: 2 - 22/zoom（因为向上放大 1/zoom 倍）
  // 工具条: 抗缩放 scale(1/zoom) + transformOrigin='bottom center'
  //   → 底边位置 = top (因 bottom center 使底边固定)
  //   → 要让底边 = 标题视觉顶部 - 5px 间距
  //   → 但 CSS top 是元素顶边, 元素高≈30px, 所以 top = 底边 - 30
  const titleVisualTop = 2 - 22 / zoom;
  const dynamicTop = titleVisualTop - 5 - 30;

  return (
    <div ref={toolbarRef} className="nodrag" style={{
      position: 'absolute',
      top: dynamicTop,
      left: '50%',
      transform: `translateX(-50%) scale(${1 / zoom})`,
      transformOrigin: 'bottom center',
      zIndex: 20,
    }}>
      {/* 主工具条 — 胶囊形 */}
      <div style={capsuleBar}>
        {/* Logo + 标题 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 2 }}>
          {toolboxLogo ? (
            <img src={toolboxLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <MjLogo />
          )}
          <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' }}>
            {toolboxName || 'MJ 工具箱'}
          </span>
        </div>

        <div style={divider} />

        {isGrid ? (
          /* ── Stage 1: Grid 阶段按钮 (重做 / 变体 / 放大) ── */
          <>
            {!hidden.has('reroll') && <ToolButton icon={RefreshCw} label={actionLabels.reroll || '重做'} onClick={() => handleAction('reroll')} />}
            {!hidden.has('variation') && <ToolButton icon={Shuffle} label={actionLabels.variation || '变体'} onClick={() => setPopup(popup === 'variation' ? null : 'variation')} />}
            {!hidden.has('upscale') && <ToolButton icon={ZoomIn} label={actionLabels.upscale || '放大'} onClick={() => setPopup(popup === 'upscale' ? null : 'upscale')} />}
          </>
        ) : (
          /* ── Stage 2: Upscaled 阶段按钮（根据 API 返回的 mjButtons 动态显示） ── */
          <>
            {canShow('subtle_upscale') && <ToolButton icon={Sparkles} label={actionLabels.subtle_upscale || '细腻放大'} onClick={() => handleAction('subtle_upscale')} />}
            {canShow('creative_upscale') && <ToolButton icon={Palette} label={actionLabels.creative_upscale || '创意放大'} onClick={() => handleAction('creative_upscale')} />}
            {canShow('zoom') && <ToolButton icon={Maximize} label={actionLabels.zoom || '缩放'} onClick={() => setPopup(popup === 'zoom' ? null : 'zoom')} />}
            {canShow('pan') && <ToolButton icon={Move} label={actionLabels.pan || '平移'} onClick={() => setPopup(popup === 'pan' ? null : 'pan')} />}
            {canShow('high_variation') && <ToolButton icon={Wand2} label={actionLabels.high_variation || '强变体'} onClick={() => handleAction('high_variation')} />}
            {canShow('low_variation') && <ToolButton icon={Wand2} label={actionLabels.low_variation || '弱变体'} onClick={() => handleAction('low_variation')} />}
            {canShow('inpaint') && <ToolButton icon={Paintbrush} label={actionLabels.inpaint || '局部重绘'} onClick={() => handleAction('inpaint')} />}
          </>
        )}

        {/* 积分显示 — 与左侧文字等高（fontSize 9），灰白色低调风格 */}
        {creditCost > 0 && (
          <>
            <div style={divider} />
            <QuickTooltip content="查看积分明细" position="top" delay={200}>
              <div
                onClick={() => window.dispatchEvent(new CustomEvent('open-credit-logs'))}
                style={{
                  fontSize: 9, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.06)',
                  padding: '2px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 2,
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              >
                <Sparkle size={9} color="rgba(255,255,255,0.7)" strokeWidth={2.5} style={{ marginRight: 1 }} /> {creditCost}
              </div>
            </QuickTooltip>
          </>
        )}
      </div>

      {/* 弹窗层 */}
      {popup === 'upscale' && (
        <GridSelector label="放大" onSelect={(i) => handleAction('upscale', { index: i })} onClose={() => setPopup(null)} />
      )}
      {popup === 'variation' && (
        <GridSelector label="变体" onSelect={(i) => handleAction('variation', { index: i })} onClose={() => setPopup(null)} />
      )}
      {popup === 'zoom' && (
        <ZoomSelector onSelect={(level) => handleAction('zoom', { zoomLevel: level })} onClose={() => setPopup(null)} />
      )}
      {popup === 'pan' && (
        <PanSelector onSelect={(dir) => handleAction('pan', { panDirection: dir })} onClose={() => setPopup(null)} />
      )}
    </div>
  );
}

// ── 弹窗样式 ──
const popupStyles = {
  container: {
    position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 4,
    background: 'rgba(30, 30, 30, 0.25)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    padding: 6, zIndex: 30, width: 140,
    boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
  },
  title: {
    fontSize: 9, color: 'rgba(255,255,255,0.6)', marginBottom: 4,
    textAlign: 'center', fontWeight: 500,
  },
  grid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: 3,
  },
  cell: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', aspectRatio: '1 / 1',
    background: 'rgba(255,255,255,0.08)', borderRadius: 4,
    cursor: 'pointer', color: '#fff', transition: 'background 0.15s',
  },
  option: {
    padding: '4px 8px', borderRadius: 3, cursor: 'pointer',
    color: '#fff', fontSize: 11, textAlign: 'center',
    transition: 'background 0.15s',
  },
};
